import { UPDATE_ENTITIES } from "EntitiesApi/Actions";
import { getRecommendationSurveyByCompanyProfileId } from "EntitiesApi/api";
import get from "lodash/get";
import { isPrimepayContinueSync } from "Primepay/isPrimepay";
import {
  SET_FILTER,
  RESET_FILTER,
  DENTAL_PLANS_REQUESTED,
  ALL_DENTAL_PLANS_REQUESTED,
  VISION_PLANS_REQUESTED,
  MEC_PLANS_REQUESTED,
  MVP_PLANS_REQUESTED,
  UPDATE_DENTAL_PLANS,
  UPDATE_ALL_DENTAL_PLANS,
  UPDATE_VISION_PLANS,
  UPDATE_MEC_PLANS,
  UPDATE_MVP_PLANS,
  PLAN_SUITE_REQUESTED,
  EMPLOYEE_SYNC_REQUESTED,
  INITIAL_STATE_LOAD_REQUESTED,
  INITIAL_STATE_LOAD_FINISHED,
  INITIAL_STATE_LOAD_SUCCESS,
  RECOMMENDATION_SURVEY_LOADED,
  SET_LIMITED_VALUE_PLANS_FILTER,
  RESET_LIMITED_VALUE_PLANS_FILTER,
} from "QuotePage/Actions";
import { SET_DENTAL_FILTER, RESET_DENTAL_FILTER } from "QuotePage/app/DentalQuotePage/Actions";
import { getFilters } from "QuotePage/Selectors";
import { isRenewal } from "QuotePage/urls";
import { takeLatest, delay } from "redux-saga";
import { fork, take, call, put, select } from "redux-saga/effects";
import _ from "underscore";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";

import { RECEIVE_PLANS } from "Common/PlanLoader/Actions";
import { getPlans } from "Common/PlanLoader/Selectors";

import { getAllDentalPlans } from "./app/DentalQuotePage/Selectors";
import { companyIdToken, history } from "./Store";

function fetchDentalPlansApi(companyIdToken) {
  return fetchWithCSRFToken(`/small-group/select-plan/ajax-quote-dental-plans?company_id_token=${companyIdToken}`, {
    method: "GET",
  }).then((response) => response.json());
}

function fetchAllDentalPlansApi(companyProfile) {
  return fetchWithCSRFToken(`/small-group/select-plan/ajax-quote-all-dental-plans?company_id_token=${companyProfile.id_token}`, {
    method: "GET",
  }).then((response) => response.json());
}

function fetchVisionPlansApi(companyIdToken) {
  return fetchWithCSRFToken(`/small-group/select-plan/ajax-quote-vision-plans?company_id_token=${companyIdToken}`, {
    method: "GET",
  }).then((response) => response.json());
}

function fetchMecPlansApi(companyIdToken) {
  return fetchWithCSRFToken(`/small-group/select-plan/ajax-quote-mec-plans?company_id_token=${companyIdToken}`, {
    method: "GET",
  }).then((response) => response.json());
}

function fetchMvpPlansApi(companyIdToken) {
  return fetchWithCSRFToken(`/small-group/select-plan/ajax-quote-mvp-plans?company_id_token=${companyIdToken}`, {
    method: "GET",
  }).then((response) => response.json());
}

function updateFiltersApi(filters, companyProfile) {
  const idToken = companyProfile.id_token || companyProfile.idToken;
  return fetchWithCSRFToken(`/small-group/select-plan/update-filters?company_id_token=${idToken}`, {
    method: "POST",
    body: JSON.stringify({ filters }),
  });
}

function* updateFilters() {
  const filters = yield select(getFilters);
  const companyProfile = yield select((state) => state.companyProfile);
  yield call(updateFiltersApi, filters, companyProfile);
}

export function* FiltersSaga() {
  yield takeLatest(
    [SET_FILTER, RESET_FILTER, SET_DENTAL_FILTER, RESET_DENTAL_FILTER, SET_LIMITED_VALUE_PLANS_FILTER, RESET_LIMITED_VALUE_PLANS_FILTER],
    updateFilters,
  );
}

// Check the store on a loop for a loaded company profile
function* waitForCompanyProfile() {
  while (true) {
    const companyProfile = yield select((state) => state.companyProfile);
    if (Object.keys(companyProfile).length !== 0) {
      return companyProfile;
    }
    yield delay(100);
  }
}

function* updateDentalPlans(action) {
  let companyIdToken = action?.payload?.companyIdToken;
  if (!companyIdToken) {
    const { id_token, idToken } = yield call(waitForCompanyProfile);
    companyIdToken = id_token || idToken;
  }
  const response = yield call(fetchDentalPlansApi, companyIdToken);
  yield put({ type: UPDATE_DENTAL_PLANS, plans: response.results });
}

export function* RefreshDentalPlans() {
  yield takeLatest(DENTAL_PLANS_REQUESTED, updateDentalPlans);
}

function* updateAllDentalPlans() {
  const companyProfile = yield call(waitForCompanyProfile);
  const plans = yield select(getAllDentalPlans);
  if (Object.keys(plans).length > 0) {
    return plans;
  }
  const response = yield call(fetchAllDentalPlansApi, companyProfile);
  yield put({ type: UPDATE_ALL_DENTAL_PLANS, plans: response.results });
}

export function* RefreshAllDentalPlans() {
  yield takeLatest(ALL_DENTAL_PLANS_REQUESTED, updateAllDentalPlans);
}

function* updateVisionPlans(action) {
  let companyIdToken = action?.payload?.companyIdToken;
  if (!companyIdToken) {
    const { id_token, idToken } = yield call(waitForCompanyProfile);
    companyIdToken = id_token || idToken;
  }
  const response = yield call(fetchVisionPlansApi, companyIdToken);
  yield put({ type: UPDATE_VISION_PLANS, plans: response.results });
}

export function* RefreshVisionPlans() {
  yield takeLatest(VISION_PLANS_REQUESTED, updateVisionPlans);
}

function* updateMecPlans(action) {
  let companyIdToken = action?.payload?.companyIdToken;
  if (!companyIdToken) {
    const { id_token, idToken } = yield call(waitForCompanyProfile);
    companyIdToken = id_token || idToken;
  }
  const response = yield call(fetchMecPlansApi, companyIdToken);
  yield put({ type: UPDATE_MEC_PLANS, plans: response.results });
}

export function* RefreshMecPlans() {
  yield takeLatest(MEC_PLANS_REQUESTED, updateMecPlans);
}

function* updateMvpPlans(action) {
  let companyIdToken = action?.payload?.companyIdToken;
  if (!companyIdToken) {
    const { id_token, idToken } = yield call(waitForCompanyProfile);
    companyIdToken = id_token || idToken;
  }
  const response = yield call(fetchMvpPlansApi, companyIdToken);
  yield put({ type: UPDATE_MVP_PLANS, plans: response.results });
}

export function* RefreshMvpPlans() {
  yield takeLatest(MVP_PLANS_REQUESTED, updateMvpPlans);
}

function primepayContinueSync() {
  return fetchWithCSRFToken("/primepay/continue-sync", {
    method: "POST",
  }).then(primepayContinueSyncFlowResponse);
}

function primepayContinueSyncFlowResponse(response) {
  if (response.ok) {
    if (response.redirected) {
      window.location = response.url;
    } else {
      return response.json();
    }
  } else {
    throw new Error("Unable to complete Primepay sync");
  }
}

function fetchInitialDataApi(company_id_token) {
  const employerFlow = isRenewal() ? "employer-renewal" : "small-group";
  let resourceUrl = `/${employerFlow}/ajax-quote-page-initial-data`;

  if (company_id_token) {
    resourceUrl += `?company_id_token=${company_id_token}`;
  }

  return fetchWithCSRFToken(resourceUrl, {
    method: "GET",
    dataType: "json",
  }).then((response) => {
    if (response.status === 422) {
      return response.json().then(({ error, redirect_url = "/error_500" }) => {
        const url = new URL(redirect_url, window.location.origin);
        if (error) {
          url.searchParams.set("notice", error);
          url.searchParams.set("severity", "warning");
        }
        window.location.href = url.toString();
        return new Promise(() => null); // stops the saga
      });
    }

    return response.json();
  });
}

// Used for all routes in QuotePage/App and nowhere else.
export function* hydrateStore(parsed) {
  let results;

  if (isPrimepayContinueSync()) {
    results = yield call(primepayContinueSync, parsed);
    history.replace("/primepay/router/small-group/select-plan");
  } else {
    results = yield call(fetchInitialDataApi, parsed.company_id_token || companyToken());
  }

  if (results.success) {
    yield call(companyIdToken.set, results.companyProfile.id_token);

    const initialState = {
      companyProfile: results.companyProfile,
      employeeProfile: results.companyProfile.employeeProfile.length > 0 ? results.companyProfile.employeeProfile : void 0,
      carriersInRegion: results.carriers,
      planTypes: results.plan_types,
      filters: results.filters,
      companyPolicyId: results.companyPolicyId,
      savedPlanIDs: results.savedPlanIDs,
      favoritePlanIDs: results.favoritePlanIDs,
      recommendedPlans: results.recommendedPlans,
      abTests: results.abTests,
      userExperienceFlags: results.userExperienceFlags,
      eligibility: results.eligibility,
      cart: results.cart,
      relatedCartPlanIds: results.relatedCartPlanIds,
      trackingParams: results.trackingParams,
      planPackages: results.planPackages,
      allCarriersMaxOfferedPlans: results.allCarriersMaxOfferedPlans,
      medicalPlanEnrolledId: results.medicalPlanEnrolledId,
      medicalPlanRenewalId: results.medicalPlanRenewalId,
      renewalPolicy: results.renewalPolicy,
      nextQuarterPricingAvailable: results.nextQuarterPricingAvailable,
      siteConfig: results.siteConfig,
      newCostBreakdown: {
        contributionScheme: {
          ratioSelf: results.contributionEmployee,
          ratioFamily: results.contributionDependent,
        },
      },

      city: results.city,
      state: results.state,
      dentalPlans: results.dentalPlans,
      visionPlans: results.visionPlans,
    };

    if (results.medicalPlans.length > 0) {
      initialState.allPlans = results.medicalPlans;
    }

    if (results.dentalPlans.length > 0) {
      yield put({
        type: DENTAL_PLANS_REQUESTED,
      });
    }

    if (results.visionPlans.length > 0) {
      yield put({
        type: VISION_PLANS_REQUESTED,
      });
    }

    yield put({
      type: INITIAL_STATE_LOAD_SUCCESS,
      initialState,
    });

    if (results.siteConfig.primepay) {
      yield put({ type: EMPLOYEE_SYNC_REQUESTED });
    }

    yield call(fillDynamicAccountData, results);
    yield put({
      type: INITIAL_STATE_LOAD_FINISHED,
    });
  } else {
    const redirect_url = results.location || "/";
    window.location = redirect_url;
  }
}

// TODO - ZeroRuby has most of this stuff set up through the snippets interface.
// There's a few things that will have to stay here, indicated below.
function* fillDynamicAccountData(data) {
  // Segment Calls
  if (window.analytics) {
    // TODO - trackingParams.abTests isn't in ZeroRuby, but the account ID is.
    if (data.currentAccount.id) {
      let trackingId;
      // Launch date of New Composite ID
      if (new Date(data.companyProfile.createdAt) > new Date(2019, 6, 14)) {
        trackingId = `acct_${data.currentAccount.id}_company_${data.companyProfile.id}`;
      } else {
        trackingId = `acct_${data.currentAccount.id}`;
      }

      yield call(window.analytics.alias, trackingId);
      yield call(window.analytics.identify, trackingId, data.trackingParams.abTests);
    } else {
      yield call(window.analytics.identify, data.trackingParams.abTests);
    }
  }

  if (window.smartlook) {
    window.smartlook("identify", data.currentAccount.id, {
      email: data.currentAccount.email,
    });
  }
}

function fetchPlanSuite() {
  const queryString = require("query-string");
  const { zipcode } = queryString.parse(window.location.search);
  return fetchWithCSRFToken(`/api/v2/plans/default-plan-suite${zipcode ? `?zipcode=${zipcode}` : ""}`, { method: "GET" }).then((response) => response.json());
}

function* requestPlanSuite() {
  const planSuite = yield call(fetchPlanSuite);
  const currPlans = yield select(getPlans);
  // If we already have plans in the Store, ignore the plans from the
  // plan suite, we don't need them anymore.
  if (currPlans && currPlans.length > 0) {
    return;
  }
  if (planSuite.medicalPlans && planSuite.medicalPlans.length > 0) {
    yield put({ type: RECEIVE_PLANS, plans: planSuite.medicalPlans });
  }
}

export function* watchRequestPlanSuite() {
  yield takeLatest(PLAN_SUITE_REQUESTED, requestPlanSuite);
}

function requestEmployeeSyncApi(idToken) {
  return fetchWithCSRFToken(`/primepay/sync-employee-data${idToken ? `?id_token=${idToken}` : ""}`, {
    method: "POST",
  }).then((response) => response.json());
}

function* requestEmployeeSync() {
  yield call(requestEmployeeSyncApi, companyToken());
}

export function* watchRequestEmployeeSync() {
  yield takeLatest(EMPLOYEE_SYNC_REQUESTED, requestEmployeeSync);
}

export function* watchRequestInitialData() {
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  yield takeLatest(INITIAL_STATE_LOAD_REQUESTED, hydrateStore, parsed);
}

export function* RecommendationSurveySaga() {
  const initialData = yield take(INITIAL_STATE_LOAD_SUCCESS);

  yield put(getRecommendationSurveyByCompanyProfileId(initialData.initialState.companyProfile.id));

  yield fork(function* () {
    let isRequestComplete = false;
    while (!isRequestComplete) {
      const action = yield take(UPDATE_ENTITIES);
      if (get(action, "payload.entities.recommendationSurveys")) {
        yield put({ type: RECOMMENDATION_SURVEY_LOADED });
        isRequestComplete = true;
      }
    }
  });
}
